import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ReceiveApiAdapter } from '../adapters/ReceiveApiAdapter';
import { apiClientWH } from '@shared/services/ApiClient';

export const useLocateReturned = (returnedId: number) => {
  const { t } = useTranslation('receiveModule');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // 🎯 Adapter para ubicar productos
  const receiveAdapter = new ReceiveApiAdapter(apiClientWH);

  // 🎯 Mutation para ubicar productos (línea por línea)
  const locateMutation = useMutation({
    mutationFn: (data: {
      lines: Array<{
        product_id: number;
        quantity: number;
        location_id: number;
      }>;
    }) => receiveAdapter.locateReturnedLines(returnedId, data),
    onSuccess: (response, variables) => {
      // 🎯 Mostrar toast de éxito para cada ubicación individual
      toast({
        title: t('toasts.locate.success'),
        description: (response as any)?.message || t('toasts.locate.success'),
        variant: 'default',
      });

      // 🎯 Invalidar queries relacionadas
      queryClient.invalidateQueries({
        queryKey: ['returnedListForReceive', returnedId],
      });
      queryClient.invalidateQueries({ queryKey: ['logisticStatusCounts'] });
      queryClient.invalidateQueries({ queryKey: ['returned'] });
    },
    onError: (error: any) => {
      console.log('🔍 useLocateReturned - Error:', error);

      let errorMessage = t('toasts.locate.unexpected');

      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast({
        title: t('toasts.locate.error'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  return {
    locateProducts: locateMutation.mutate,
    locateProductsAsync: locateMutation.mutateAsync,
    isLocating: locateMutation.isPending,
  };
};
